import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from './PageHeaderSection';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Standard list/detail page shell — matches Enrollments layout.
 * Back navigation is rendered above the hero card via PageBackSection (ListPageHeader / PageHeaderSection).
 */
export default function ListPageLayout({ children, className = '' }) {
    return (
        <div className={`list-page ${className}`.trim()}>
            {children}
        </div>
    );
}

export function ListPageHeader({
    title,
    subtitle,
    actions,
    eyebrow,
    children,
    showBack = true,
    backTo = null,
    className = '',
}) {
    return (
        <PageHeaderSection
            title={title}
            subtitle={subtitle}
            eyebrow={eyebrow}
            aside={actions}
            showBack={showBack}
            backTo={backTo}
            className={className}
        >
            {children}
        </PageHeaderSection>
    );
}

export { default as PageHeaderSection } from './PageHeaderSection';
export { default as PageBackSection } from './PageBackSection';

export function ListFilterTabs({ tabs, active, onChange }) {
    return (
        <div className="list-filter-tabs">
            <div className="list-filter-tabs-scroll border-b border-gray-200">
                <nav className="flex min-w-max -mb-px">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => onChange(tab.key)}
                            className={`list-filter-tab ${active === tab.key ? 'list-filter-tab-active' : ''}`}
                        >
                            {tab.label}
                            {' '}
                            <span className="ml-2 text-xs">({tab.count ?? 0})</span>
                        </button>
                    ))}
                </nav>
            </div>
        </div>
    );
}

export function ListSearchCard({ value, onChange, placeholder, shown, total, entityLabel }) {
    const { t } = useTranslation();
    const label = entityLabel ?? t('common.items');
    return (
        <div className="list-search-card">
            <div className="list-search-card-glow" />
            <div className="list-search-card-inner">
                <div className="relative">
                    <svg
                        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M12.9 14.32a8 8 0 111.414-1.414l3.387 3.387a1 1 0 01-1.414 1.414l-3.387-3.387zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <input
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        className="list-search-input"
                    />
                </div>
                <div className="mt-2 text-sm text-gray-500 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    {t('common.showing')}{' '}
                    <span className="font-semibold text-purple-600 mx-1">{shown ?? 0}</span>
                    {t('common.of')}{' '}
                    <span className="font-semibold text-gray-700 mx-1">{total ?? 0}</span>
                    {label}
                </div>
            </div>
        </div>
    );
}

export function ListLoadingSpinner({ label = null } = {}) {
    return <LoadingSpinner size="md" label={label} />;
}

export function ListEmptyState({ message, hint }) {
    return (
        <div className="list-empty-state">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-500 font-medium">{message}</p>
            {hint && <p className="text-gray-400 text-sm mt-2">{hint}</p>}
        </div>
    );
}

export function ListTableCard({ children, className = '' }) {
    return (
        <div className={`list-table-card overflow-hidden ${className}`.trim()}>
            {children}
        </div>
    );
}

export function ListSectionCard({ children, className = '' }) {
    return (
        <div className={`list-section-card ${className}`.trim()}>
            {children}
        </div>
    );
}
